import React from "react";
import { alpha, useTheme } from "@mui/material/styles";
import { Box, Button, IconButton, Slider, Stack, Tooltip, Typography } from "@mui/material";
import VolumeUpRoundedIcon from "@mui/icons-material/VolumeUpRounded";
import VolumeOffRoundedIcon from "@mui/icons-material/VolumeOffRounded";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import PauseRoundedIcon from "@mui/icons-material/PauseRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import RepeatRoundedIcon from "@mui/icons-material/RepeatRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import GraphicEqRoundedIcon from "@mui/icons-material/GraphicEqRounded";

const fmt = (t) => new Date(t * 1000).toISOString().substr(14, 5);

const FALLBACK_WAVEFORM_BARS = Array.from({ length: 96 }, (_, i) => {
  const signal = Math.abs(
    (Math.sin(i * 0.37) * 0.55)
    + (Math.sin((i * 0.11) + 1.7) * 0.32)
    + (Math.sin((i * 0.71) + 0.4) * 0.18)
  );
  return Math.min(0.95, 0.14 + (signal * 0.72));
});

const SHOWCASE_ACCENT = "#F9F5EE";
const SHOWCASE_ACCENT_DARK = "#DED9D1";
const SHOWCASE_TEXT = "#F9F5EE";
const SHOWCASE_CONTROL_INK = "#1A1A1A";
const AUDIO_COMPARE_START_EVENT = "wavemind:audio-compare-start";
let audioCompareInstanceSequence = 0;

export default function AudioCompare({ section, dense = false, crossfadeMs = 0, appearance = "default" }) {
  const theme = useTheme();
  const showcase = appearance === "showcase";

  const [active, setActive] = React.useState(0);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [duration, setDuration] = React.useState(0);
  const [time, setTime] = React.useState(0);
  const [loop, setLoop] = React.useState(true);
  const [isScrubbing, setIsScrubbing] = React.useState(false);

  const tracks = React.useMemo(() => {
    if (Array.isArray(section?.audios) && section.audios.length) return section.audios;
    if (section?.audioSrc) return [{ src: section.audioSrc, label: section.audioLabel }];
    return [];
  }, [section]);

  const refs = React.useRef([]);
  const fadeFrame = React.useRef(null);
  const instanceId = React.useRef(null);
  if (instanceId.current === null) {
    audioCompareInstanceSequence += 1;
    instanceId.current = `audio-compare-${audioCompareInstanceSequence}`;
  }
  const getRef = (i) => (el) => (refs.current[i] = el);

  const cancelCrossfade = React.useCallback(() => {
    if (fadeFrame.current !== null) {
      window.cancelAnimationFrame(fadeFrame.current);
      fadeFrame.current = null;
    }
  }, []);

  // collect duration from all tracks (take max)
  const handleLoadedMeta = (e) => {
    const d = e.currentTarget.duration;
    if (!Number.isNaN(d) && d !== Infinity) {
      setDuration((prev) => Math.max(prev, d));
    }
  };

  // Keep only the selected track audible when playback is stopped or
  // crossfading is disabled.
  const applyActiveMute = React.useCallback((activeIndex = active) => {
    cancelCrossfade();
    refs.current.forEach((a, i) => {
      if (!a) return;
      a.muted = i !== activeIndex;
      a.volume = i === activeIndex ? 1 : 0;
    });
  }, [active, cancelCrossfade]);

  React.useEffect(() => {
    if (!isPlaying || crossfadeMs <= 0) applyActiveMute();
  }, [applyActiveMute, crossfadeMs, isPlaying]);

  React.useEffect(() => () => cancelCrossfade(), [cancelCrossfade]);

  const crossfadeTo = React.useCallback((nextActive) => {
    cancelCrossfade();

    const durationMs = Math.max(0, Number(crossfadeMs) || 0);
    if (durationMs === 0) {
      refs.current.forEach((a, i) => {
        if (!a) return;
        a.muted = i !== nextActive;
        a.volume = i === nextActive ? 1 : 0;
      });
      return;
    }

    const startingVolumes = refs.current.map((a) => a && !a.muted ? a.volume : 0);
    refs.current.forEach((a, i) => {
      if (!a) return;
      if (i === nextActive || startingVolumes[i] > 0) a.muted = false;
    });

    const startedAt = window.performance.now();
    const step = (now) => {
      const progress = Math.min((now - startedAt) / durationMs, 1);
      const eased = progress * progress * (3 - (2 * progress));

      refs.current.forEach((a, i) => {
        if (!a) return;
        const targetVolume = i === nextActive ? 1 : 0;
        const volume = startingVolumes[i] + ((targetVolume - startingVolumes[i]) * eased);
        a.volume = Math.max(0, Math.min(1, volume));
      });

      if (progress < 1) {
        fadeFrame.current = window.requestAnimationFrame(step);
        return;
      }

      refs.current.forEach((a, i) => {
        if (!a) return;
        a.muted = i !== nextActive;
        a.volume = i === nextActive ? 1 : 0;
      });
      fadeFrame.current = null;
    };

    fadeFrame.current = window.requestAnimationFrame(step);
  }, [cancelCrossfade, crossfadeMs]);

  // Start all audios at the same time from `t`
  const startAllAt = async (t = 0) => {
    window.dispatchEvent(new CustomEvent(AUDIO_COMPARE_START_EVENT, {
      detail: { instanceId: instanceId.current },
    }));

    // prepare
    refs.current.forEach((a) => {
      if (!a) return;
      a.loop = loop;
      a.muted = true;       // temporarily mute all to satisfy Safari autoplay
      a.playsInline = true;
      try { a.currentTime = t; } catch { }
    });
    applyActiveMute();

    // play all
    try {
      await Promise.all(refs.current.map(async (a) => {
        if (!a) return;
        if (a.paused) await a.play();
      }));
      setIsPlaying(true);
    } catch {
      // If Safari blocked, the user needs to press Play after a gesture.
    }
  };

  const pauseAll = () => {
    refs.current.forEach((a) => a && a.pause());
    applyActiveMute();
    setIsPlaying(false);
  };

  React.useEffect(() => {
    const pauseWhenAnotherComparisonStarts = (event) => {
      if (event.detail?.instanceId === instanceId.current) return;
      refs.current.forEach((audio) => audio && audio.pause());
      applyActiveMute();
      setIsPlaying(false);
    };

    window.addEventListener(AUDIO_COMPARE_START_EVENT, pauseWhenAnotherComparisonStarts);
    return () => window.removeEventListener(AUDIO_COMPARE_START_EVENT, pauseWhenAnotherComparisonStarts);
  }, [applyActiveMute]);

  const restartAll = () => {
    const wasPlaying = isPlaying;
    pauseAll();
    refs.current.forEach((a) => { if (a) a.currentTime = 0; });
    setTime(0);
    if (wasPlaying) startAllAt(0);
  };

  // Commit a seek: pause -> align -> resume (if needed)
  const commitSeek = (t) => {
    const wasPlaying = isPlaying;
    pauseAll();
    refs.current.forEach((a) => { if (a) a.currentTime = t; });
    setTime(t);
    if (wasPlaying) startAllAt(t);
  };

  // Lightweight drift check: if any track drifts a lot, re-align occasionally
  React.useEffect(() => {
    if (!isPlaying) return;
    const id = setInterval(() => {
      const master = refs.current[active];
      if (!master) return;
      const mt = master.currentTime || 0;
      let needsRealign = false;
      for (const a of refs.current) {
        if (!a || a === master) continue;
        if (Math.abs((a.currentTime || 0) - mt) > 0.35) { // ~350ms
          needsRealign = true; break;
        }
      }
      if (needsRealign) {
        const wasPlaying = true;
        pauseAll();
        refs.current.forEach((a) => { if (a) a.currentTime = mt; });
        if (wasPlaying) startAllAt(mt);
      }
    }, 700); // infrequent to avoid stutter
    return () => clearInterval(id);
  }, [isPlaying, active]);

  // Keep the transport and playhead feeling responsive without coupling the
  // synchronization check to UI rendering.
  React.useEffect(() => {
    if (!isPlaying || isScrubbing) return;
    const id = setInterval(() => {
      const master = refs.current[active];
      if (master) setTime(master.currentTime || 0);
    }, 100);
    return () => clearInterval(id);
  }, [active, isPlaying, isScrubbing]);

  const togglePlay = () => (isPlaying ? pauseAll() : startAllAt(time));

  const toggleLoop = () => {
    const v = !loop;
    setLoop(v);
    refs.current.forEach((a) => { if (a) a.loop = v; });
  };

  const onTrackClick = (idx) => {
    if (idx === active) return;
    if (isPlaying && crossfadeMs > 0) crossfadeTo(idx);
    else applyActiveMute(idx);
    setActive(idx);
  };

  if (tracks.length === 0) return null;

  const playheadPercent = duration ? Math.min(time / duration, 1) * 100 : 0;
  const waveformColor = SHOWCASE_ACCENT;
  const waveformBars = tracks[active]?.waveform?.length
    ? tracks[active].waveform
    : FALLBACK_WAVEFORM_BARS;

  if (showcase) {
    return (
      <Stack spacing={2} sx={{ mt: 2 }}>
        <Box
          sx={{
            position: "relative",
            height: { xs: 126, sm: 148 },
            overflow: "hidden",
            border: "1px solid rgba(249, 245, 238, 0.16)",
            borderRadius: 1.5,
            bgcolor: "#111",
            backgroundImage: "linear-gradient(rgba(249,245,238,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(249,245,238,0.045) 1px, transparent 1px)",
            backgroundSize: "100% 24px, 8.333% 100%",
            boxShadow: "inset 0 0 30px rgba(0, 0, 0, 0.62)",
            "&:focus-within": { borderColor: waveformColor, boxShadow: `inset 0 0 30px rgba(0, 0, 0, 0.62), 0 0 0 2px ${alpha(waveformColor, 0.2)}` },
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ position: "absolute", zIndex: 3, top: 8, left: 12, right: 12 }}>
            <Typography variant="overline" sx={{ color: waveformColor, fontWeight: 800, fontSize: "0.62rem", letterSpacing: "0.13em" }}>
              Track {String(active + 1).padStart(2, "0")} · {tracks[active]?.label}
            </Typography>
            <Typography variant="caption" sx={{ color: "rgba(249,245,238,0.74)", fontFamily: "monospace", fontVariantNumeric: "tabular-nums" }}>
              {fmt(time)} / {duration ? fmt(duration) : "--:--"}
            </Typography>
          </Stack>

          <Box component="svg" viewBox="0 0 960 100" preserveAspectRatio="none" aria-hidden sx={{ position: "absolute", left: 10, right: 10, bottom: 10, width: "calc(100% - 20px)", height: "calc(100% - 42px)" }}>
            <line x1="0" y1="50" x2="960" y2="50" stroke="rgba(249,245,238,0.14)" strokeWidth="1" />
            {waveformBars.map((level, idx) => {
              const slotWidth = 960 / waveformBars.length;
              const barWidth = Math.max(2, slotWidth * 0.52);
              const x = idx * slotWidth;
              const height = Math.max(6, level * 88);
              const played = ((idx + 1) / waveformBars.length) * 100 <= playheadPercent;
              return (
                <rect
                  key={idx}
                  x={x + ((slotWidth - barWidth) / 2)}
                  y={50 - (height / 2)}
                  width={barWidth}
                  height={height}
                  rx="1.5"
                  fill={played ? waveformColor : "rgba(249,245,238,0.28)"}
                  style={{ transition: "fill 100ms linear" }}
                />
              );
            })}
          </Box>

          <Box sx={{ position: "absolute", zIndex: 4, top: 31, bottom: 0, left: `${playheadPercent}%`, width: "2px", bgcolor: SHOWCASE_TEXT, boxShadow: "0 0 8px rgba(249, 245, 238, 0.58)", pointerEvents: "none", transform: "translateX(-1px)" }} />
          <Box sx={{ position: "absolute", zIndex: 4, top: 27, left: `${playheadPercent}%`, width: 0, height: 0, borderLeft: "5px solid transparent", borderRight: "5px solid transparent", borderTop: `7px solid ${SHOWCASE_TEXT}`, transform: "translateX(-5px)", pointerEvents: "none" }} />

          <Slider
            aria-label="Seek"
            min={0}
            max={duration || 1}
            step={0.01}
            value={Math.min(time, duration || 1)}
            disabled={!duration}
            onChange={(_, value) => {
              const nextTime = Array.isArray(value) ? value[0] : value;
              setIsScrubbing(true);
              setTime(nextTime);
            }}
            onChangeCommitted={(_, value) => {
              const nextTime = Array.isArray(value) ? value[0] : value;
              setIsScrubbing(false);
              commitSeek(nextTime);
            }}
            sx={{ position: "absolute", zIndex: 5, inset: 0, width: "100%", height: "100%", p: 0, opacity: 0, cursor: duration ? "crosshair" : "default" }}
          />
        </Box>

        <Stack direction="row" spacing={{ xs: 1, sm: 1.5 }} alignItems="center">
          <IconButton
            aria-label={isPlaying ? "Pause" : "Play"}
            onClick={togglePlay}
            sx={{
              flexShrink: 0,
              width: { xs: 46, sm: 52 },
              height: { xs: 46, sm: 52 },
              color: SHOWCASE_CONTROL_INK,
              bgcolor: SHOWCASE_ACCENT,
              boxShadow: "0 8px 24px rgba(249, 245, 238, 0.2)",
              "&:hover": { bgcolor: SHOWCASE_ACCENT_DARK },
            }}
          >
            {isPlaying ? <PauseRoundedIcon /> : <PlayArrowRoundedIcon />}
          </IconButton>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="overline" sx={{ display: "block", color: "rgba(249, 245, 238, 0.48)", fontSize: "0.6rem", lineHeight: 1.2, letterSpacing: "0.14em" }}>
              Transport
            </Typography>
            <Typography variant="caption" sx={{ color: isPlaying ? waveformColor : "rgba(249, 245, 238, 0.76)", fontWeight: 700 }}>
              {isPlaying ? "Playback running" : "Playback stopped"}
            </Typography>
          </Box>

          <IconButton
            aria-label="Restart"
            onClick={restartAll}
            sx={{ color: "rgba(249, 245, 238, 0.82)", border: "1px solid rgba(249, 245, 238, 0.16)", "&:hover": { bgcolor: "rgba(249, 245, 238, 0.08)" } }}
          >
            <ReplayRoundedIcon />
          </IconButton>
          <Button
            size="small"
            startIcon={<RepeatRoundedIcon />}
            aria-pressed={loop}
            onClick={toggleLoop}
            sx={{
              flexShrink: 0,
              minWidth: 0,
              px: 1.4,
              color: loop ? SHOWCASE_TEXT : "rgba(249, 245, 238, 0.62)",
              bgcolor: loop ? "rgba(249, 245, 238, 0.15)" : "transparent",
              border: `1px solid ${loop ? "rgba(249, 245, 238, 0.62)" : "rgba(249, 245, 238, 0.16)"}`,
              borderRadius: 999,
              "&:hover": { bgcolor: loop ? alpha(SHOWCASE_ACCENT_DARK, 0.24) : "rgba(249, 245, 238, 0.08)" },
              "& .MuiButton-startIcon": { mr: { xs: 0, sm: 0.5 } },
            }}
          >
            <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>Loop</Box>
          </Button>
        </Stack>

        <Stack direction="row" spacing={1.25} alignItems="center" justifyContent="flex-end">
          <Typography variant="overline" sx={{ mr: 0.5, color: "rgba(249,245,238,0.48)", fontSize: "0.6rem", letterSpacing: "0.14em" }}>
            Monitor source
          </Typography>
          {tracks.map((a, idx) => (
            <Tooltip key={a.src || idx} title={a.label || `Clip ${idx + 1}`} arrow>
              <IconButton
                aria-label={a.label || `Clip ${idx + 1}`}
                aria-pressed={active === idx}
                onClick={() => onTrackClick(idx)}
                sx={{
                  width: 44,
                  height: 40,
                  color: active === idx ? SHOWCASE_TEXT : "rgba(249,245,238,0.52)",
                  bgcolor: active === idx ? alpha(waveformColor, 0.2) : "rgba(249,245,238,0.035)",
                  border: `1px solid ${active === idx ? waveformColor : "rgba(249,245,238,0.16)"}`,
                  borderRadius: 1.25,
                  boxShadow: active === idx ? `0 0 16px ${alpha(waveformColor, 0.18)}` : "none",
                  "&:hover": { color: SHOWCASE_TEXT, bgcolor: active === idx ? alpha(SHOWCASE_ACCENT_DARK, 0.24) : "rgba(249,245,238,0.09)" },
                }}
              >
                {idx === 0 ? <LayersRoundedIcon /> : <GraphicEqRoundedIcon />}
                <audio
                  ref={getRef(idx)}
                  preload="none"
                  src={a.src}
                  loop={loop}
                  playsInline
                  onLoadedMetadata={handleLoadedMeta}
                  style={{ display: "none" }}
                  aria-hidden
                />
              </IconButton>
            </Tooltip>
          ))}
        </Stack>
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5} sx={{ mt: 2 }}>
      {/* Controls */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button size="small" variant="outlined" onClick={togglePlay}>
          {isPlaying ? "Pause" : "Play"}
        </Button>
        <Button size="small" variant="text" onClick={restartAll}>Restart</Button>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56, textAlign: "right" }}>
          {fmt(time)} / {duration ? fmt(duration) : "--:--"}
        </Typography>
      </Stack>

      {/* Seek bar (don’t live-seek; commit on release) */}
      <Box sx={{ px: 0.5 }}>
        <input
          type="range"
          min={0}
          max={duration || 0}
          step={0.01}
          value={isScrubbing ? undefined : time}
          onChange={(e) => {
            const t = parseFloat(e.target.value);
            setIsScrubbing(true);
            setTime(t); // update display only
          }}
          onMouseUp={(e) => { setIsScrubbing(false); commitSeek(parseFloat(e.currentTarget.value)); }}
          onTouchEnd={(e) => {
            const input = e.currentTarget;
            setIsScrubbing(false);
            commitSeek(parseFloat(input.value));
          }}
          onKeyUp={(e) => {
            if (["ArrowLeft", "ArrowRight", "Home", "End"].includes(e.key)) {
              const t = parseFloat(e.currentTarget.value);
              setIsScrubbing(false);
              commitSeek(t);
            }
          }}
          style={{ width: "100%" }}
          aria-label="Seek"
        />
      </Box>

      {/* Loop */}
      <Stack direction="row" spacing={1} alignItems="center">
        <Button size="small" variant={loop ? "contained" : "outlined"} onClick={toggleLoop}>
          {loop ? "Loop: On" : "Loop: Off"}
        </Button>
      </Stack>

      {/* Track list */}
      <Stack spacing={1}>
        {tracks.map((a, idx) => (
          <Box key={a.src || idx}
            onClick={() => onTrackClick(idx)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onTrackClick(idx); } }}
            role="button"
            tabIndex={0}
            aria-pressed={active === idx}
            sx={{
              cursor: 'pointer', outline: 'none',
              transition: 'border-color 120ms ease, background-color 120ms ease',
              '&:hover': { borderColor: theme.palette.text.secondary, backgroundColor: alpha(theme.palette.background.paper, 0.75) },
              '&:focus-visible': { borderColor: theme.palette.primary.main, boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.25)}` },
              borderRadius: 2,
              border: `1px solid ${active === idx ? theme.palette.primary.main : theme.palette.divider}`,
              p: dense ? 1 : 1.25,
              bgcolor: active === idx ? alpha(theme.palette.primary.main, 0.12) : alpha(theme.palette.background.paper, 0.6),
              backdropFilter: "saturate(140%) blur(4px)",
            }}>
            <Stack spacing={0.25}>
              <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                <Typography variant="caption" sx={{ fontWeight: 500, fontSize: '0.7rem' }}>
                  {a.label || `Clip ${idx + 1}`}
                </Typography>
                <IconButton
                  size="small"
                  color={active === idx ? 'primary' : 'default'}
                  aria-label={`Listen to ${a.label || `clip ${idx + 1}`}`}
                  onClick={(e) => { e.stopPropagation(); onTrackClick(idx); }}
                >
                  {active === idx ? <VolumeUpRoundedIcon /> : <VolumeOffRoundedIcon />}
                </IconButton>
              </Stack>
              {a.caption && (
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                  {a.caption}
                </Typography>
              )}
              {/* Hidden audio elements */}
              <audio
                ref={getRef(idx)}
                preload="none"
                src={a.src}
                loop={loop}
                playsInline
                onLoadedMetadata={handleLoadedMeta}
                // DO NOT do any per-frame sync here; we keep it simple.
                style={{ display: "none" }}
                aria-hidden
              />
            </Stack>
          </Box>
        ))}
      </Stack>
    </Stack>
  );
}
